import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Tooltip, Empty } from 'antd';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import { EventRanking } from '../EventRanking';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import Spinner from '../../../../components/Spinner';

import './index.scss';

const TOTAL_DURATION_QUESTION_ID = 289;

const formatDurationString = (durationInput) => {
  let hours, minutes, seconds;

  if (typeof durationInput === 'number') {
    const totalSeconds = durationInput;
    hours = Math.floor(totalSeconds / 3600);
    minutes = Math.floor((totalSeconds % 3600) / 60);
    seconds = totalSeconds % 60;
  } else if (typeof durationInput === 'string') {
    const parts = durationInput.split(':');
    if (parts.length !== 3) return durationInput;

    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
    seconds = parseInt(parts[2], 10);
  } else {
    return '0s';
  }

  const durationParts = [];
  if (hours > 0) durationParts.push(`${hours}h`);
  if (minutes > 0) durationParts.push(`${minutes}m`);
  if (seconds > 0 || durationParts.length === 0)
    durationParts.push(`${seconds}s`);

  return durationParts.join(' ');
};

const TotalDurationRanking = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  selectedDate,
}) => {
  const [data, setData] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { namespace } = useSelector((state) => state);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!isActive) {
      setShowAll(false);
    }
  }, [isActive]);

  const baseFetchData = useMonitoringDataFetch(
    TOTAL_DURATION_QUESTION_ID,
    selectedDate,
  );

  const fetchTotalDurationData = useCallback(async () => {
    if (!namespace) return null;

    try {
      setIsLoading(true);
      const rawData = await baseFetchData();
      const transformedData = rawData.map((item, index) => ({
        id: item.serial,
        index: String(index + 1).padStart(2, '0'),
        name: item.name,
        duration: formatDurationString(item.duration),
        trend: 'right',
      }));

      setData(transformedData);
      if (isMountedRef.current) {
        setIsLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
      }
      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload } = useMonitoringDataWithInterval(
    fetchTotalDurationData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  React.useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  useEffect(() => {
    if (isActive && isLoading && !loadingTimeout) {
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) {
          setLoadingTimeout(true);
        }
      }, 30000);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isActive, isLoading, loadingTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  const displayedData = showAll ? data : data.slice(0, 5);

  // const getTrendIcon = (trend) => {
  //   switch (trend) {
  //     case 'up':
  //       return <ArrowUpOutlined className="trend-icon trend-up" />;
  //     case 'down':
  //       return <ArrowDownOutlined className="trend-icon trend-down" />;
  //     case 'right':
  //       return <ArrowRightOutlined className="trend-icon trend-right" />;
  //     default:
  //       return null;
  //   }
  // };

  const content = isLoading ? (
    <>
      <Spinner />
      {loadingTimeout && <Empty description="No data" />}
    </>
  ) : (
    <div className="total-duration-ranking">
      <div className="total-duration-ranking-list">
        {displayedData?.length > 0 ? (
          displayedData.map((item) => (
            <div key={item.id} className="total-duration-ranking-item">
              <div className="col-index">{item.index}</div>
              <Tooltip title={item.name}>
                <div className="col-name">{item.name}</div>
              </Tooltip>
              <div className="col-duration">{item.duration}</div>
              {/* <div className="col-trend">{getTrendIcon(item.trend)}</div> */}
            </div>
          ))
        ) : (
          <Empty description="No data" />
        )}
      </div>
    </div>
  );

  return (
    <EventRanking
      headerIcon={<ClockCircleOutlined />}
      headerTitle="Top Total Duration"
      headerSubTitle="Cumulative (24h)"
      showAll={showAll}
      onToggleAll={() => setShowAll(!showAll)}
      hasMore={data.length > 5}
    >
      {content}
    </EventRanking>
  );
};

export default TotalDurationRanking;
